import { Box, Button, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { palette, spaces } from '../../assets/styles/themes/tokens';
import FooterLegalNavigation from '../../components/login/FooterLegalNavigation';
import PageContainer from '../../components/login/PageContainer';
import OnboardingHeader from '../../components/onboarding/OnboardingHeader';
import useScreenSize from '../../utils/useScreenSize';

interface ChangeEmailConfirmedPageProps {
	newEmail: string;
	resendEmail: () => void;
	changeEmailAddress: () => void;
	isLoading: boolean;
}

const ChangeEmailConfirmedPage: React.FC<ChangeEmailConfirmedPageProps> = ({
	newEmail,
	resendEmail,
	changeEmailAddress,
	isLoading,
}) => {
	const { t } = useTranslation();
	const { isXsScreen, isSmScreen } = useScreenSize();

	return (
		<PageContainer pageBodySx={{ gap: 0 }}>
			<Box
				data-testid="change-email-confirmed-page"
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: isXsScreen ? spaces.xl : spaces.xxxl,
				}}
			>
				<OnboardingHeader
					title={t('Success!')}
					subtitle={t('A confirmation E-Mail was sent successfully.')}
				/>
				<Box
					sx={{
						height: 'fit-content',
					}}
				>
					<Typography
						variant={isSmScreen ? 'body2' : 'body1'}
						sx={{
							color: palette.text.secondary,
							textAlign: 'center',
							marginTop: spaces.md,
						}}
					>
						{t('Please click on the link within to confirm the change')}
					</Typography>
					<Box
						sx={{
							marginTop: spaces.lg,
							display: 'flex',
							justifyContent: 'center',
						}}
					>
						<Typography variant="h6" sx={{ color: 'text.primary' }}>
							{newEmail}
						</Typography>
					</Box>
				</Box>
			</Box>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					marginTop: 'auto',
					gap: spaces.lg,
				}}
			>
				<Button
					data-testid="resend-email-button"
					variant="outlined"
					size="large"
					color="primary"
					onClick={resendEmail}
					disabled={isLoading}
					sx={{
						minWidth: '140px',
					}}
				>
					{isLoading ? (
						<CircularProgress
							size={22}
							sx={{
								color: 'inherit',
								'& .MuiCircularProgress-svg': {
									width: 'auto',
									height: 'auto',
								},
							}}
						/>
					) : (
						t('Resend Link')
					)}
				</Button>
				<Button
					data-testid="change-email-address-button"
					variant="text"
					size="medium"
					color="secondary"
					onClick={changeEmailAddress}
				>
					{t('Change E-Mail Address')}
				</Button>
			</Box>
			<FooterLegalNavigation isSmallScreen={isXsScreen} />
		</PageContainer>
	);
};

export default ChangeEmailConfirmedPage;
